import React from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

interface ConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: "delete" | "logout" | "default";
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  open,
  onClose,
  onConfirm,
  title = "Are you sure?",
  description = "This action cannot be undone.",
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "default",
}) => {
  const isDelete = variant === "delete";
  const isLogout = variant === "logout";

  return (
    <AlertDialog open={open} onOpenChange={onClose}>
      <AlertDialogContent
        className={cn(
          // Base styling
          "rounded-2xl shadow-lg border fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 border-gray-200 text-center p-6 bg-[#18181B]",
          // Responsive width
          "w-[90%] sm:w-full max-w-sm",
          // Mobile-friendly padding
          "mx-auto sm:my-0"
        )}
      >
        <AlertDialogHeader className="space-y-2">
          {/* Title */}
          <AlertDialogTitle className="text-lg sm:text-xl font-semibold text-white">
            {title}
          </AlertDialogTitle>

          {/* Description */}
          <AlertDialogDescription className="text-sm text-[#BABABB]">
            {description}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter className="flex flex-col sm:flex-row justify-between gap-3">
          <AlertDialogCancel
            className={cn(
              "w-full sm:w-auto bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium px-5 py-2 rounded-lg transition-colors"
            )}
          >
            {cancelText}
          </AlertDialogCancel>

          <AlertDialogAction
            onClick={onConfirm}
            className={cn(
              "w-full sm:w-auto text-white font-medium px-5 py-2 rounded-lg transition-colors",
              isDelete || isLogout
                ? "bg-red-600 hover:bg-red-500"
                : "bg-gray-800 hover:bg-gray-700"
            )}
          >
            {confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ConfirmModal;
